import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
import { useMutation } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';
import { fg } from '@confluence/feature-gating';

import { colorCodeToColorName, tokenToHex } from '../helpers';

import { SetContentStateMutation } from './SetContentStateMutation.graphql';
import { EditorContentStateQuery } from './EditorContentStateQuery.graphql';
import { ContentState } from './ContentState';
import { i18n } from './i18n';
import type { ContentStateGroupProps, ContentState as ContentStateType } from './types';
import { customGroupLabel } from './types';
import { createNewCustomStatesArrayWhenExistingCustomSelected } from './helpers';
import type {
	SetContentStateMutation as SetContentStateMutationType,
	SetContentStateMutationVariables,
} from './__types__/SetContentStateMutation';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupLabel = styled.div({
	marginBottom: token('space.150', '4px'),
	color: token('color.text.subtle', N200),
	fontSize: '11px',
	lineHeight: '11px',
	marginLeft: token('space.300', '24px'),
	textTransform: 'uppercase',
	fontWeight: 'bold',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateListWrapper = styled.ul({
	listStyleType: 'none',
	margin: token('space.0', '0px'),
	padding: token('space.0', '0px'),
});

export const ContentStateGroupComponent: FC<ContentStateGroupProps> = ({
	contentId,
	contentStateState,
	groupIsEnabled,
	groupLabel,
	contentStates,
	flags,
	userOffline,
	intl: { formatMessage },
	onUpdateSuccess,
	setIsContentStateMenuOpen,
}) => {
	// Need these refs to store original content states/array
	// in the case of an error (ex: user picks a state but somehow admin turned off at the same time) we need to
	// show the original values since we set it right after we mutate and there's no way to check if there are any
	// errors until after we mutate
	const originalContentState = useRef(contentStateState.state.contentState);
	const originalCustomStatesArray = useRef(contentStateState.state.customStatesArray);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(false);

	useEffect(() => {
		if (
			!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		) {
			setShouldSetAutomationFlag(true);
		}
	}, []);

	const isLivePage = useIsLivePage();

	const [setContentStateMutation] = useMutation<
		SetContentStateMutationType,
		SetContentStateMutationVariables
	>(SetContentStateMutation, {
		onError: () => {
			// rollback to originals since they got updated and show error flag
			void contentStateState.setContentState(originalContentState.current);
			void contentStateState.setCustomStatesArray(originalCustomStatesArray.current);
			void flags.showErrorFlag({
				id: 'content-state-error-flag',
				title: <FormattedMessage {...i18n.errorFlagTitle} />,
				description: <FormattedMessage {...i18n.errorFlagBody} />,
			});
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					objectId: contentId,
					name: 'contentStateErrorFlag',
					attributes: {
						isLivePage,
					},
				},
			}).fire();
		},
		onCompleted: () => {
			// If a mutation was successfully completed, save the state/array in case an error occurs and we need to
			// rollback to these values
			originalContentState.current = contentStateState.state.contentState;
			originalCustomStatesArray.current = contentStateState.state.customStatesArray;

			void contentStateState.setIsUpdating(false);

			onUpdateSuccess?.();
		},
		// Need to refetch since our cache isn't updating correctly - temporary solution until BE fixes
		refetchQueries: () => [
			{
				query: EditorContentStateQuery,
				variables: {
					contentId,
					isDraft: true,
				},
			},
		],
	});

	const handleClick = useCallback(
		(newContentState: ContentStateType, label: string, index: number) => {
			void contentStateState.setIsUpdating(true);
			void setContentStateMutation({
				variables: {
					contentId,
					contentStateInput: {
						color: tokenToHex(newContentState.color),
						name: newContentState.name,
						id: newContentState.id,
					},
				},
			});
			void contentStateState.setContentState(newContentState);
			setIsContentStateMenuOpen(false);
			// if label selected was a custom one, updated the custom list to add the selected label on top
			if (label === customGroupLabel) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					contentStateState.state.customStatesArray,
					newContentState,
					index,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}

			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: label, // "custom" or "suggested"
						color: colorCodeToColorName(newContentState.color!.toUpperCase()),
						restrictionLevel: newContentState.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			setContentStateMutation,
			setIsContentStateMenuOpen,
			isLivePage,
			shouldSetAutomationFlag,
		],
	);

	const contentStateList = contentStates.map((contentState, index) => {
		if (contentState) {
			return (
				<ContentState
					contentState={contentState}
					userOffline={userOffline}
					onClick={() => handleClick(contentState, groupLabel, index)}
					key={`${groupLabel}-content-state-${index}`}
				/>
			);
		}
	});
	return groupIsEnabled && contentStates.length > 0 ? (
		<div data-testid={`${groupLabel.toLowerCase()}-content-states`}>
			<GroupLabel aria-label={groupLabel}>
				{groupLabel === customGroupLabel
					? formatMessage(i18n.custom)
					: formatMessage(i18n.suggested)}
			</GroupLabel>
			<ContentStateListWrapper aria-label={groupLabel}>{contentStateList}</ContentStateListWrapper>
		</div>
	) : null;
};

export const ContentStateGroup = injectIntl(withFlags(ContentStateGroupComponent));
